import { useEffect, useRef, useState } from 'react'

import { PolylineProps } from '@editor/interfaces/canvas'
import { calculateIndexInPolyline } from '@editor/services/Polyline'
import { Line2, LineGeometry } from 'three-stdlib'

import useConfigs from './useConfigs'

const useEdgeHighlight = (
  { points }: PolylineProps,
  { pointOnLine, isRemovingVertex }: ReturnType<typeof useConfigs>,
) => {
  // States
  const [isEdgeHighlightVisible, setIsEdgeHighlightVisible] = useState(false)
  const [edgeGeometry] = useState(() => {
    const geom = new LineGeometry()
    geom.setPositions([0, 1, 0, 0, 0, 0]) // edge highlight can only ever be 2 points so we set it once here
    return geom
  })

  // Refs
  const edgeHighlightRef = useRef<Line2>(null)

  /**
   * Update the edge highlight positions based on the current edge index.
   * This highlights edges when hovering over them in Remove Vertex mode for edge cutting.
   */
  useEffect(() => {
    if (!pointOnLine || !isRemovingVertex) {
      setIsEdgeHighlightVisible(false)
      return
    }

    const index = calculateIndexInPolyline(points, pointOnLine)
    const startPoint = points[index - 1]
    const endPoint = points[index]

    if (!startPoint || !endPoint) {
      setIsEdgeHighlightVisible(false)
      return
    }

    edgeGeometry.setPositions([...startPoint, ...endPoint])
    setIsEdgeHighlightVisible(true)
  }, [edgeGeometry, points, pointOnLine, isRemovingVertex])

  return {
    edgeHighlightRef,
    edgeGeometry,

    isEdgeHighlightVisible,
  }
}

export default useEdgeHighlight
